// ChatContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { CookieHelper } from "./aiClientStorage";
import { apiCall } from "./aiservice";
import { groupConversationsByLatestActivity } from "./utils";
import { ChunkData } from "./chunk";
import { sendEvent } from "../../modules/helpers/Analytics";

export const aiServiceBaseUrl = "/api/ai/1.0";

const ChatContext = createContext<ChatContextType | undefined>(undefined);

type SourceType = {
  Chunks: Array<{
      ChunkId: string;
      Index: string;
  }>;
  IndexName: string;
  SourceId: string;
  Title: string;
  Type: string;
};

type PostType = {
  Content: string;
  Role: string;
  CreatedDate: string;
  Sources: Array<SourceType>;
};

type MessageResponseBodyType = {
  ConversationId: number;
  Question: string;
};

export type CurrentConversationType = {
  Id: number;
  UserId: string;
  Posts: Array<PostType>;
  ConversationTitle: string;
  CreatedDateUtc: string;
  UpdatedDateUtc: string;
} | null;

export type AIContentType = {
  aiAssistantProfilePicture: string;
  aiLogotype: string;
  loginUrl: string;
  aiSubHeader: string;
  description: string;
  aiHeader: string;
  aiName: string;
  aiSearchFieldPlaceholder_ContinousConversation: string;
  aiSearchFieldPlaceholder_NewConversation: string;
  searchExamples: string[];
  mainLogo: string;
  isActiveUser: boolean;
  aI_Enabled: boolean;
  userName: string;
};

export type ConversationGroupType =
  | "today"
  | "yesterday"
  | "last7Days"
  | "last30days"
  | "older";

export type ConversationHistoryType = {
  ConversationId: number;
  ConversationTitle: string;
  LatestActivity: string;
  IsFavorite: boolean;
};

export type SortedConversationHistoryType = {
  groupName: ConversationGroupType;
  data: Array<ConversationHistoryType>;
};

export type ConversationContainer = {
  FavoriteConversations: Array<ConversationHistoryType>;
  Conversations: Array<SortedConversationHistoryType>;
}

export type AiModalState = "full" | "small" | "mini";

interface ChatContextType {
  aiContent: AIContentType | undefined;
  currentConversation: CurrentConversationType;
  questionValue: string;
  startNewChat: (e?: React.MouseEvent) => void;
  setQuestionValue: (q: string) => void;
  setCurrentConversation: (conversation: CurrentConversationType) => void;
  callGetConversationWithId: (id: number) => void;
  handleSend: (message: string) => Promise<void>;
  loading: boolean;
  setLoading: (state: boolean) => void;
  error: string | null;
  allConversations: ConversationContainer;
  setAllConversations: (
    sortedConversation: ConversationContainer
  ) => void;
  callGetAllConversations: () => void;
  aiModalState: AiModalState;
  setAiModalState: (newState: AiModalState) => void;
}

export const ChatProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const storedAiUsage = CookieHelper.readAiUsage();

  const [currentConversation, setCurrentConversation] =
    React.useState<CurrentConversationType>();
  const [aiModalState, setAiModalState] = React.useState<AiModalState>(
    storedAiUsage?.modalState ?? "small"
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiContent, setAIContent] = React.useState<AIContentType>();
  const [questionValue, setQuestionValue] = React.useState<string>("");
  const [allConversations, setAllConversations] = React.useState<ConversationContainer>({ FavoriteConversations: [], Conversations: [] });

  // GetAllChatHistory
  const callGetAllConversations = async () => {
    apiCall(aiServiceBaseUrl + "/getallconversationsforuser")
      .then((data: string) => {
        if (!!data) {
          let parsed_allConversations = JSON.parse(
            data
          ) as ConversationContainer;
          //parsed_allConversations = parsed_allConversations.reverse();
          parsed_allConversations.Conversations = groupConversationsByLatestActivity(parsed_allConversations.Conversations);
          setAllConversations(parsed_allConversations);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  // GetChatHistory
  const callGetConversationWithId = async (id: number) => {
    apiCall(aiServiceBaseUrl + "/getconversation/" + id.toString())
      .then((data) => {
        if (!!data) {
          let conversation = JSON.parse(data) as CurrentConversationType;
          setCurrentConversation(conversation);
        }
      })
      .catch((error) => console.error(error));
  };

    const handleSendAnalyticsEvent = async (id: string, title: string, indexName: string) => {
        if (indexName) {
            apiCall(aiServiceBaseUrl + "/getchunk/" + id + "/" + indexName)
            .then((data: string) => {
                if (!!data) {
                    try {
                        let parsedChunk = JSON.parse(data) as ChunkData;
                        sendEvent("ai-source", undefined, {
                            pageTitle: title,
                            documentType: parsedChunk.document?.types[0],
                        });
                    } catch (e) {
                        console.info("Chunk error: ", e);
                        return null;
                    }
                }
            })
            .catch((error) => {
                sendEvent("ai-source", undefined, {
                    pageTitle: title,
                });
                console.info("chunk error: ", error);
                return null;
            });
        }
  };

  const updateConversationInList = (parsedData: CurrentConversationType) => {
    if (!parsedData) return;
    const updatedSummary: ConversationHistoryType = {
      ConversationId: parsedData.Id,
      ConversationTitle: parsedData.ConversationTitle,
      LatestActivity: parsedData.UpdatedDateUtc,
      IsFavorite: false,
    };
    setAllConversations(prev => {
      const allFlat = (prev?.Conversations ?? []).flatMap(g => g.data);
      const exists = allFlat.some(c => c.ConversationId === parsedData.Id);
      const updatedFlat = exists
        ? allFlat.map(c => c.ConversationId === parsedData.Id ? updatedSummary : c)
        : [updatedSummary, ...allFlat];
      return {
        FavoriteConversations: prev?.FavoriteConversations ?? [],
        Conversations: groupConversationsByLatestActivity(updatedFlat),
      };
    });
  };

  const handleSend = async (customQuery?: string) => {
    const messageResponseBody = {
      ConversationId: currentConversation?.Id ?? 0,
      Question: customQuery ?? questionValue,
    } as MessageResponseBodyType;

    setLoading(true);

    apiCall(
      aiServiceBaseUrl + "/getmessageresponse",
      "POST",
      messageResponseBody
    )
      .then((data: string) => {
        try {
          if (!!data) {
            const parsedData = JSON.parse(data) as CurrentConversationType;
            parsedData?.Posts[parsedData?.Posts.length - 1].Sources.forEach(
                (source) => {
                    handleSendAnalyticsEvent(
                        source.Chunks[0].ChunkId,
                        source.Title,
                        source.Chunks[0].Index ?? source.IndexName
                );
              }
            );

            sendEvent("ai-search-message", undefined, {
              question: messageResponseBody?.Question,
              answer: parsedData?.Posts[parsedData?.Posts.length - 1].Content,
              nrOfSources: parsedData?.Posts[parsedData?.Posts.length - 1].Sources.length
            });

            setCurrentConversation(parsedData);
            updateConversationInList(parsedData);
          }
          else{
            sendEvent("ai-search-error", undefined, {
              question: messageResponseBody?.Question
            });
          }

        } catch (e) {
          sendEvent("ai-search-error", undefined, {
            question: messageResponseBody?.Question
          });
          console.error(e);
        }
      })
      .catch((error) => {
        sendEvent("ai-search-error", undefined, {
          question: messageResponseBody?.Question
        });
        console.error(error)
      })
      .finally(() => {
        setLoading(false);
      });
  };

  function startNewChat(e?: React.MouseEvent) {
    !!e && e.preventDefault();
    setCurrentConversation(null);
  }

  function populateContent() {
    apiCall(aiServiceBaseUrl + "/getcontent")
      .then((data: AIContentType) => {
        if (!!data) {
          setAIContent(data);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

    useEffect(() => {
        populateContent();

        if (storedAiUsage?.currentConversationId) {
            callGetConversationWithId(storedAiUsage.currentConversationId);
        }

        if (window.innerWidth <= 960) {
            setAiModalState("full");
        }

        const handleResize = () => {
            if (window.innerWidth <= 960) {
                setAiModalState("full");
            }
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

  const providerObj = {
    aiContent,
    questionValue,
    setQuestionValue,
    handleSend,
    setCurrentConversation,
    currentConversation,
    loading,
    setLoading,
    startNewChat,
    error,
    allConversations,
    setAllConversations,
    callGetConversationWithId,
    callGetAllConversations,
    aiModalState,
    setAiModalState,
  } as ChatContextType;

  return (
    <ChatContext.Provider value={{ ...providerObj }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};
